import type { PropertyCommunicationField, PropertyCommunicationPlaceholder } from '@/shared/api';
import { splitTokens, type Segment, type SegmentKind } from '@/shared/components';

export const PORTAL_LINK = '{{GastportalLink}}';

/** Jedes dynamische Feld, das es gibt — auch die, die hier nicht erlaubt sind, samt Grund. */
export const KNOWN_FIELDS: Record<string, { label: string; unavailable: string; why: string }> = {
  '{{GastVorname}}': {
    label: 'Gastvorname',
    unavailable: 'nur E-Mail',
    why: 'OTA-Nachrichten gehen gesammelt je Reservierung raus und tragen keine Gastdaten.',
  },
  '{{Apartment}}': { label: 'Apartment', unavailable: 'nicht verfügbar', why: '' },
  '{{Objekt}}': { label: 'Objekt', unavailable: 'nicht verfügbar', why: '' },
  '{{Anreisedatum}}': { label: 'Anreisedatum', unavailable: 'nicht verfügbar', why: '' },
  '{{Abreisedatum}}': { label: 'Abreisedatum', unavailable: 'nicht verfügbar', why: '' },
  [PORTAL_LINK]: {
    label: 'Gastportal-Link',
    unavailable: 'nicht nach der Abreise',
    why: 'Die Nachricht nach dem Aufenthalt kennt dieses Feld nicht.',
  },
};

/** Wie der Server prüft: ein Feld gilt nur, wo seine Liste es für genau dieses Textfeld erlaubt. */
function classify(
  token: string,
  placeholders: PropertyCommunicationPlaceholder[],
  field: PropertyCommunicationField | null,
): SegmentKind {
  const offered = placeholders.find((item) => item.token === token);
  if (offered) {
    if (!field || !offered.allowedFields.includes(field)) return 'misplaced';
    return token === PORTAL_LINK ? 'link' : 'field';
  }
  return KNOWN_FIELDS[token] ? 'misplaced' : 'unknown';
}

/** Zerlegt einen Text für die Hervorhebung, klassifiziert wie der Server. */
export function segments(
  value: string,
  placeholders: PropertyCommunicationPlaceholder[],
  field: PropertyCommunicationField | null,
): Segment[] {
  return splitTokens(value, (token) => classify(token, placeholders, field));
}

/** Ein bekanntes Feld, das dem Getippten nahekommt — für „Gemeint ist wohl …". */
export function suggestion(token: string) {
  const typed = token.replace(/[{}]/g, '').toLowerCase();
  let best: { label: string; distance: number } | null = null;
  for (const [known, { label }] of Object.entries(KNOWN_FIELDS)) {
    const distance = levenshtein(typed, known.replace(/[{}]/g, '').toLowerCase());
    if (!best || distance < best.distance) best = { label, distance };
  }
  return best && best.distance <= Math.max(3, Math.floor(typed.length / 2)) ? best.label : null;
}

function levenshtein(a: string, b: string) {
  const row = Array.from({ length: b.length + 1 }, (_, index) => index);
  for (let i = 1; i <= a.length; i += 1) {
    let previous = row[0]!;
    row[0] = i;
    for (let j = 1; j <= b.length; j += 1) {
      const current = row[j]!;
      row[j] = Math.min(row[j]! + 1, row[j - 1]! + 1, previous + (a[i - 1] === b[j - 1] ? 0 : 1));
      previous = current;
    }
  }
  return row[b.length]!;
}
